import { chaveDeFotoDeCategoriaSchema } from '@locaveri/shared'
import { ImagePlus, LoaderCircle, Star, Trash2, TriangleAlert } from 'lucide-react'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useUrlsDeLeitura } from '@/features/arquivos/url-de-leitura'
import { errorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'

import { useEnviarFotoDeCategoria } from './api'

/**
 * A GALERIA da categoria — o uploader que substituiu o campo de URLs coladas.
 *
 * ---------------------------------------------------------------------------
 * O QUE ESTAVA ERRADO ANTES
 * ---------------------------------------------------------------------------
 * Até aqui a galeria era um `<Textarea>` de "uma URL por linha" e
 * `POST /files/category-photo` existia sem ninguém chamar. As duas metades
 * nunca se encontravam, e o resultado só aparecia em produção: quem colasse a
 * URL de um objeto do NOSSO bucket cadastrava uma foto que não abre para
 * ninguém — o bucket é privado —, e quem colasse a URL de um site qualquer
 * cadastrava uma foto que some no dia em que aquele site sair do ar. Nos dois
 * casos, sem erro em lugar nenhum: o campo aceitava, o catálogo mostrava
 * imagem quebrada, e a página que deveria vender a categoria vendia um retângulo
 * vazio.
 *
 * Agora o que o formulário guarda é a CHAVE devolvida pelo upload assinado. A
 * vitrine pública serve cada foto por REDIRECIONAMENTO ASSINADO a partir dela, e
 * o bucket continua fechado — que é o único arranjo aceitável quando `documentos`
 * (CNH do cliente), `vistorias` e `sinistros` são pastas irmãs desta.
 *
 * ---------------------------------------------------------------------------
 * A URL CRUA CONTINUA SENDO EXIBIDA, E ISSO É DE PROPÓSITO
 * ---------------------------------------------------------------------------
 * A galeria aceita chave OU URL por uma release (P8), porque há categorias com
 * URLs gravadas do jeito antigo. Elas aparecem aqui marcadas como legadas, para
 * serem trocadas quando alguém passar por elas — e não somem sozinhas: apagar
 * silenciosamente a foto de uma categoria seria pior do que a foto ruim.
 *
 * ---------------------------------------------------------------------------
 * A MINIATURA DE UMA CHAVE CUSTA UMA ASSINATURA
 * ---------------------------------------------------------------------------
 * O bucket é privado também para o painel, então cada chave já gravada vira uma
 * URL de leitura assinada por `useUrlsDeLeitura` (cache de 4 min, abaixo do TTL
 * da assinatura). A foto RECÉM-ENVIADA não passa por lá: ela usa o `objectURL`
 * do arquivo local que acabou de subir — é instantâneo, não gasta assinatura, e
 * evita a janela em que o objeto ainda não é legível.
 */

/** O `.max(20)` de `criarCategoriaSchema.galeria`; o Zod é quem manda. */
const MAXIMO_DE_FOTOS = 20

/** O que o upload assinado aceita em `FOTO_DE_CATEGORIA`. */
const TIPOS_ACEITOS = 'image/jpeg,image/png,image/webp'

export interface GaleriaDeCategoriaProps {
  /** Chaves do storage e, transitoriamente, URLs cruas gravadas antes. */
  readonly valor: readonly string[]
  readonly aoMudar: (proximo: string[]) => void
  readonly desabilitado?: boolean
}

export function GaleriaDeCategoria({
  valor,
  aoMudar,
  desabilitado = false,
}: GaleriaDeCategoriaProps): ReactElement {
  const enviar = useEnviarFotoDeCategoria()
  const [enviando, setEnviando] = useState(0)

  const chaves = valor.filter((item) => ehChave(item))
  const { urlPorChave } = useUrlsDeLeitura(chaves)

  /**
   * Miniaturas locais das fotos desta sessão, por chave.
   *
   * `useRef` e não estado: o valor não decide render nenhum sozinho (a lista de
   * fotos é do formulário) e revogar os `objectURL` no desmonte precisa da
   * referência estável — um estado recriaria o efeito de limpeza a cada envio.
   */
  const previaLocal = useRef(new Map<string, string>())
  useEffect(() => {
    const previas = previaLocal.current

    return () => {
      for (const url of previas.values()) {
        URL.revokeObjectURL(url)
      }
    }
  }, [])

  const cheia = valor.length >= MAXIMO_DE_FOTOS
  const ocupado = desabilitado || enviando > 0

  // O `valor` mais recente que o pai passou. Um `ref` atualizado a cada render é
  // a forma de o laço de upload ler o estado de AGORA: os envios são
  // sequenciais mas assíncronos, e somar sobre o `valor` capturado no closure
  // perderia a foto anterior do mesmo lote.
  const valorRef = useRef<readonly string[]>(valor)
  valorRef.current = valor

  function aoEscolher(lista: FileList | null): void {
    if (lista === null || lista.length === 0) {
      return
    }

    // O corte acontece ANTES de subir: enviar cinco fotos para descobrir no
    // submit que só cabiam duas gasta a franquia de upload da pessoa e ainda
    // deixa três objetos órfãos no bucket.
    const arquivos = Array.from(lista).slice(0, MAXIMO_DE_FOTOS - valor.length)
    if (arquivos.length < lista.length) {
      toast.warning(`A galeria aceita até ${String(MAXIMO_DE_FOTOS)} fotos.`)
    }
    if (arquivos.length === 0) {
      return
    }

    setEnviando((atual) => atual + arquivos.length)

    void (async () => {
      for (const arquivo of arquivos) {
        try {
          const enviada = await enviar.mutateAsync(arquivo)
          previaLocal.current.set(enviada.chave, URL.createObjectURL(arquivo))
          aoMudar([...valorRef.current, enviada.chave])
        } catch (causa) {
          toast.error('Não foi possível enviar a foto', { description: errorMessage(causa) })
        } finally {
          setEnviando((atual) => atual - 1)
        }
      }
    })()
  }

  function remover(indice: number): void {
    aoMudar(valor.filter((_, posicao) => posicao !== indice))
  }

  function promoverACapa(indice: number): void {
    const foto = valor[indice]
    if (foto === undefined) {
      return
    }
    aoMudar([foto, ...valor.filter((_, posicao) => posicao !== indice)])
  }

  return (
    <div className="space-y-3">
      {valor.length === 0 ? (
        <p className="text-sm text-fg-muted">
          Nenhuma foto ainda. A primeira que você enviar vira a CAPA — é ela que o cliente vê na
          lista do catálogo e no link compartilhado.
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {valor.map((foto, indice) => (
            <li
              key={`${foto}-${String(indice)}`}
              className="space-y-2 rounded-lg border border-border bg-surface p-2"
            >
              <Miniatura
                foto={foto}
                url={previaLocal.current.get(foto) ?? urlPorChave.get(foto) ?? enderecoLegado(foto)}
                capa={indice === 0}
              />

              <div className="flex items-center justify-between gap-1">
                {indice === 0 ? (
                  <Badge variant="outline">Capa</Badge>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      promoverACapa(indice)
                    }}
                    disabled={ocupado}
                  >
                    <Star aria-hidden="true" />
                    Capa
                  </Button>
                )}

                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`Remover a foto ${String(indice + 1)}`}
                  onClick={() => {
                    remover(indice)
                  }}
                  disabled={ocupado}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <label
        className={cn(
          'flex h-9 w-fit cursor-pointer items-center gap-2 rounded-lg border border-border',
          'bg-elevated px-3 text-sm font-medium text-foreground transition-colors',
          'focus-within:ring-2 focus-within:ring-ring hover:bg-accent',
          (ocupado || cheia) && 'pointer-events-none opacity-50',
        )}
      >
        {enviando > 0 ? (
          <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
        ) : (
          <ImagePlus className="size-4" aria-hidden="true" />
        )}
        {enviando > 0
          ? `Enviando ${String(enviando)}…`
          : valor.length === 0
            ? 'Enviar fotos'
            : 'Adicionar fotos'}
        <input
          type="file"
          accept={TIPOS_ACEITOS}
          multiple
          className="sr-only"
          disabled={ocupado || cheia}
          onChange={(evento) => {
            aoEscolher(evento.target.files)
            // Sem zerar, escolher o MESMO arquivo de novo não dispara change.
            evento.target.value = ''
          }}
        />
      </label>

      <p className="text-xs text-fg-muted">
        JPG, PNG ou WEBP, até 8 MB cada e {String(MAXIMO_DE_FOTOS)} no total. São fotos da CLASSE —
        nunca do carro específico, e nunca com a placa visível.
      </p>
    </div>
  )
}

interface MiniaturaProps {
  readonly foto: string
  readonly url: string | undefined
  readonly capa: boolean
}

/**
 * A imagem de uma foto da galeria.
 *
 * Três estados, e nenhum deles é tela branca: enviando/assinando (esqueleto),
 * pronta (a imagem) e legada sem endereço utilizável (o aviso). O último é o que
 * torna visível a dívida da URL crua — sem ele, uma foto que não carrega e uma
 * foto que ainda está carregando são indistinguíveis para quem edita.
 */
function Miniatura({ foto, url, capa }: MiniaturaProps): ReactElement {
  const legada = !ehChave(foto)

  if (url === undefined) {
    // Uma CHAVE sem URL ainda está esperando a assinatura de leitura; um valor
    // legado sem URL nunca vai ter uma (o endereço gravado não é `http`). Os
    // dois casos precisam de telas diferentes, senão o segundo fica girando para
    // sempre e quem edita não descobre que aquela linha está morta.
    return legada ? (
      <div
        className="flex h-24 flex-col items-center justify-center gap-1 rounded-md border
          border-border bg-elevated px-2 text-center"
      >
        <TriangleAlert className="size-4 text-warning-fg" aria-hidden="true" />
        <p className="text-xs text-warning-fg">Endereço inválido — remova e envie a foto.</p>
      </div>
    ) : (
      <div className="flex h-24 items-center justify-center rounded-md border border-border bg-elevated">
        <LoaderCircle className="size-4 animate-spin text-fg-muted" aria-hidden="true" />
        <span className="sr-only">Carregando a foto…</span>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <img
        src={url}
        alt={capa ? 'Foto de capa da categoria' : 'Foto da categoria'}
        className="h-24 w-full rounded-md border border-border object-cover"
      />
      {legada ? (
        <p className="flex items-center gap-1 text-xs text-warning-fg">
          <TriangleAlert className="size-3 shrink-0" aria-hidden="true" />
          Endereço antigo — envie a foto de novo para hospedá-la aqui.
        </p>
      ) : null}
    </div>
  )
}

/** Chave do storage (o caminho novo) ou URL crua (o legado a substituir). */
function ehChave(valor: string): boolean {
  return chaveDeFotoDeCategoriaSchema.safeParse(valor).success
}

/**
 * O endereço de uma foto legada.
 *
 * Só `http`/`https` viram `src`. Uma URL gravada com outro esquema (a coluna é
 * `jsonb` e já recebeu texto colado à mão) não é exibida — colocar `javascript:`
 * num `src` é a diferença entre um campo de texto e um vetor de execução.
 */
function enderecoLegado(valor: string): string | undefined {
  return valor.startsWith('https://') || valor.startsWith('http://') ? valor : undefined
}
